import { useEffect } from "react";
import ApacheJunctionHeroSection from "./components/HeroSection";
import ApacheJunctionStickyNav from "./components/StickyNav";
import ApacheJunctionProcessingTimeSection from "./components/ProcessingTimeSection";
import ApacheJunctionServicesSection from "./components/ServicesSection";
import ApacheJunctionLocalAreaSection from "./components/LocalAreaSection";
import ApacheJunctionTravelTimingSection from "./components/TravelTimingSection";
import ApacheJunctionDestinationsSection from "./components/DestinationsSection";
import ApacheJunctionTestimonialsSection from "./components/TestimonialsSection";
import ApacheJunctionAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import ApacheJunctionComparisonSection from "./components/ComparisonSection";
import ApacheJunctionMistakesSection from "./components/MistakesSection";
import ApacheJunctionHowItWorksSection from "./components/HowItWorksSection";
import ApacheJunctionFAQSection, { faqs } from "./components/FAQSection";
import ApacheJunctionHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import ApacheJunctionFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function ApacheJunctionPage() {
  useEffect(() => {
    document.title = "Apache Junction AZ Emergency Passport — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/apache-junction/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Apache Junction, AZ — as fast as 24 hours. A+ BBB rated registered State Dept. courier serving Pinal County snowbirds, retirees, RV travelers, and East Valley workers. Best price guarantee, 30-100% less than resellers.");
    setMeta("geo.placename", "Apache Junction, Arizona");
    setMeta("geo.region", "US-AZ");
    setMeta("geo.position", "33.4150;-111.5498");
    setMeta("ICBM", "33.4150, -111.5498");

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Arizona", item: "https://fastpassportcenter.com/arizona/" },
          { "@type": "ListItem", position: 3, name: "Apache Junction Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Emergency and expedited passport services for Apache Junction, AZ and Pinal County travelers — as fast as 24 hours. Registered U.S. Department of State courier.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: { "@type": "City", name: "Apache Junction", containedInPlace: { "@type": "State", name: "Arizona" } },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" },
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      document.getElementById("structured-data")?.remove();
      document.querySelector('link[rel="canonical"]')?.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <ApacheJunctionHeroSection />
      <ApacheJunctionStickyNav />
      <ApacheJunctionProcessingTimeSection />
      <ApacheJunctionServicesSection />
      <ApacheJunctionLocalAreaSection />
      <ApacheJunctionTravelTimingSection />
      <ApacheJunctionDestinationsSection />
      <ApacheJunctionTestimonialsSection />
      <ApacheJunctionAcceptanceFacilitiesSection />
      <ApacheJunctionComparisonSection />
      <ApacheJunctionMistakesSection />
      <ApacheJunctionHowItWorksSection />
      <ApacheJunctionFAQSection />
      <ApacheJunctionHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/apache-junction" state="AZ" />
      <ApacheJunctionFinalCTA />
    </div>
  );
}